import { useEffect, useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { Loader2, ShieldAlert, RefreshCw, ExternalLink } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// Maps an audit target onto a navigable destination in the app. Only the
// target types that correspond to a concrete, addressable row in the cash
// receipts view are linkable; everything else falls back to a plain label.
function targetLink(targetType: string | null, targetId: string | null): string | null {
  if (!targetType || !targetId) return null;
  // Numeric primary keys only — these are the ones the cash receipts view
  // can scroll to and highlight.
  if (!/^\d+$/.test(targetId)) return null;
  switch (targetType) {
    case "bank_payment":
      return `/cash-receipts?payment=${targetId}`;
    case "remittance":
      return `/cash-receipts?remittance=${targetId}`;
    default:
      return null;
  }
}

interface AuditRow {
  id: number;
  createdAt: string;
  actorUserId: number | null;
  actorEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  ip: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown> | null;
}

interface AuditResponse {
  rows: AuditRow[];
  total: number;
  limit: number;
  offset: number;
}

interface AllocationLine {
  ref: string;
  applied: number;
  balance: number;
}

interface AllocationEditMetadata {
  account: string;
  handwrittenAmount: number;
  suggested: { lines: AllocationLine[]; allocatedTotal: number; remainder: number };
  accepted: { lines: AllocationLine[]; allocatedTotal: number; remainder: number };
  diff: {
    added: { ref: string }[];
    removed: { ref: string }[];
    changed: { ref: string }[];
  };
}

type LineStatus = "added" | "removed" | "changed" | "unchanged";

function gbp(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `£${n.toFixed(2)}`;
}

// Type guard so we only attempt the rich render when the metadata genuinely
// matches the shape written by the accept-suggestion endpoint. Anything that
// doesn't match falls back to the generic JSON view.
function isAllocationEditMetadata(m: Record<string, unknown> | null): m is AllocationEditMetadata & Record<string, unknown> {
  if (!m) return false;
  const suggested = m.suggested as { lines?: unknown } | undefined;
  const accepted = m.accepted as { lines?: unknown } | undefined;
  const diff = m.diff as { added?: unknown; removed?: unknown; changed?: unknown } | undefined;
  return (
    !!suggested && Array.isArray(suggested.lines) &&
    !!accepted && Array.isArray(accepted.lines) &&
    !!diff && Array.isArray(diff.added) && Array.isArray(diff.removed) && Array.isArray(diff.changed)
  );
}

function AllocationEditDetails({ meta }: { meta: AllocationEditMetadata }) {
  const suggestedByRef = new Map(meta.suggested.lines.map((l) => [l.ref, l]));
  const acceptedByRef = new Map(meta.accepted.lines.map((l) => [l.ref, l]));

  // Preserve a stable, readable order: suggested lines first, then any
  // newly-added refs the operator introduced.
  const orderedRefs: string[] = [];
  for (const l of meta.suggested.lines) orderedRefs.push(l.ref);
  for (const l of meta.accepted.lines) if (!suggestedByRef.has(l.ref)) orderedRefs.push(l.ref);

  const rows = orderedRefs.map((ref) => {
    const s = suggestedByRef.get(ref);
    const a = acceptedByRef.get(ref);
    let status: LineStatus;
    if (s && !a) status = "removed";
    else if (!s && a) status = "added";
    else if (s && a && Math.abs(s.applied - a.applied) > 0.005) status = "changed";
    else status = "unchanged";
    const balance = a?.balance ?? s?.balance ?? 0;
    return { ref, suggested: s?.applied ?? null, accepted: a?.applied ?? null, balance, status };
  });

  const statusStyles: Record<LineStatus, { row: string; badge: string; label: string }> = {
    added: { row: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", label: "Added" },
    removed: { row: "bg-red-50", badge: "bg-red-100 text-red-700", label: "Removed" },
    changed: { row: "bg-amber-50", badge: "bg-amber-100 text-amber-700", label: "Changed" },
    unchanged: { row: "", badge: "bg-slate-100 text-slate-500", label: "Unchanged" },
  };

  return (
    <div className="min-w-[440px] max-w-[640px] space-y-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <span>Account <span className="font-medium text-slate-800">{meta.account}</span></span>
        <span>Handwritten <span className="font-medium text-slate-800">{gbp(meta.handwrittenAmount)}</span></span>
      </div>
      <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
            <th className="px-2 py-1 font-medium">Invoice</th>
            <th className="px-2 py-1 font-medium text-right">Suggested</th>
            <th className="px-2 py-1 font-medium text-right">Accepted</th>
            <th className="px-2 py-1 font-medium text-right">Balance</th>
            <th className="px-2 py-1 font-medium">Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const style = statusStyles[r.status];
            return (
              <tr key={r.ref} className={`border-t border-slate-100 ${style.row}`}>
                <td className="px-2 py-1 font-mono text-slate-800">{r.ref}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${r.status === "removed" ? "text-red-700 line-through" : "text-slate-700"}`}>
                  {gbp(r.suggested)}
                </td>
                <td className={`px-2 py-1 text-right tabular-nums ${r.status === "added" ? "text-emerald-700 font-medium" : r.status === "changed" ? "text-amber-700 font-medium" : "text-slate-700"}`}>
                  {gbp(r.accepted)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-slate-500">{gbp(r.balance)}</td>
                <td className="px-2 py-1">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${style.badge}`}>
                    {style.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-200 bg-slate-50 text-slate-700">
            <td className="px-2 py-1 font-medium">Allocated total</td>
            <td className="px-2 py-1 text-right tabular-nums">{gbp(meta.suggested.allocatedTotal)}</td>
            <td className="px-2 py-1 text-right tabular-nums font-medium">{gbp(meta.accepted.allocatedTotal)}</td>
            <td className="px-2 py-1" colSpan={2} />
          </tr>
          <tr className="border-t border-slate-100 bg-slate-50 text-slate-500">
            <td className="px-2 py-1">Unallocated</td>
            <td className="px-2 py-1 text-right tabular-nums">{gbp(meta.suggested.remainder)}</td>
            <td className="px-2 py-1 text-right tabular-nums">{gbp(meta.accepted.remainder)}</td>
            <td className="px-2 py-1" colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function AuditLogPage() {
  const { user, isLoading: authLoading, isSuperAdmin } = useAuth();
  const [, setLocation] = useLocation();
  const [data, setData] = useState<AuditResponse | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<string>("");
  const [filterActor, setFilterActor] = useState<string>("");
  const linkedId = new URLSearchParams(window.location.search).get("id");

  // Hard client-side gate — the API also enforces super-admin only,
  // this just avoids flashing a forbidden page.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLocation("/login");
    } else if (!isSuperAdmin) {
      setLocation("/");
    }
  }, [authLoading, user, isSuperAdmin, setLocation]);

  async function fetchLog() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (linkedId) params.set("id", linkedId);
      if (filterAction) params.set("action", filterAction);
      if (filterActor) params.set("actorEmail", filterActor.trim().toLowerCase());
      const res = await fetch(`${BASE}/api/audit-log?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err: any) {
      setError(err?.message ?? "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }

  async function fetchActions() {
    try {
      const res = await fetch(`${BASE}/api/audit-log/actions`, { credentials: "include" });
      if (res.ok) setActions(await res.json());
    } catch {
      // non-fatal
    }
  }

  useEffect(() => {
    if (isSuperAdmin) {
      fetchLog();
      fetchActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-slate-900">Audit log</h1>
          </div>
          <Button onClick={fetchLog} disabled={loading} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Security and admin events. Latest 200 shown. Super-admin only.
        </p>

        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Action</label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[220px]"
            >
              <option value="">All actions</option>
              {actions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Actor email</label>
            <input
              value={filterActor}
              onChange={(e) => setFilterActor(e.target.value)}
              placeholder="e.g. alex.kuness@duffells.co.uk"
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[280px]"
            />
          </div>
          <Button onClick={fetchLog} disabled={loading}>Apply</Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : data ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">
              {data.total} total event{data.total === 1 ? "" : "s"} match{data.total === 1 ? "es" : ""} filter
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <th className="px-3 py-2 font-medium">When (UTC)</th>
                    <th className="px-3 py-2 font-medium">Actor</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                    <th className="px-3 py-2 font-medium">Target</th>
                    <th className="px-3 py-2 font-medium">IP</th>
                    <th className="px-3 py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                        {new Date(row.createdAt).toISOString().replace("T", " ").replace(/\..+/, "")}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {row.actorEmail ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{row.action}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {row.targetType ? (() => {
                          const label = `${row.targetType}#${row.targetId}`;
                          const href = targetLink(row.targetType, row.targetId);
                          if (!href) return label;
                          return (
                            <button
                              type="button"
                              onClick={() => setLocation(href)}
                              className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                              title="Open the related payment in Cash Receipts"
                            >
                              {label}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          );
                        })() : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-500 font-mono text-xs">{row.ip ?? "—"}</td>
                      <td className="px-3 py-2">
                        {row.action === "REMITTANCE_ALLOCATION_EDITED" && isAllocationEditMetadata(row.metadata) ? (
                          <AllocationEditDetails meta={row.metadata} />
                        ) : row.metadata ? (
                          <pre className="text-xs text-slate-600 whitespace-pre-wrap break-all max-w-[400px]">
                            {JSON.stringify(row.metadata)}
                          </pre>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-slate-400 text-sm">
                        No events match the current filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
